import { TextChatReceiverOptionIds } from "../../screens/Dashboard/TextChat/ReceiverInput";
import { ChatMessage } from "../types";
import { TextChannelActionTypes, TextChannelActions, TextToSpeechResponse } from "./actions";
import { LogCategory, log } from "../../utils/log";

const logE = (event: string, ...args: any[]) => log(LogCategory.SpeechRecognition, "##### " + event, ...args);

export interface TextChannelState {
  workspaceId: number;
  textChannelId: number;
  messages: ChatMessage[];
  ttsAudioBytes: TextToSpeechResponse[];
  hasMore: boolean;
  currentOldestMessageId?: number;
  lastMessageTime?: string;
  speechToText?: {
    token: string;
    region: string;
    vpHost?: string;
    jwtToken?: string;
    revision?: string;
  };
  reachedMaxSttUsage: boolean;
  showChatBox: boolean;
  dmUserIds: number[] | TextChatReceiverOptionIds | undefined;
  focusChatBox: boolean;
  isLoading: boolean;
  fullScreenChatBox: boolean;
  resizeEvent: boolean;
  keyboardActive: boolean;
  selectedMessage: ChatMessage | undefined;
}

export const textChannelInitialState: TextChannelState = {
  workspaceId: 0,
  textChannelId: 0,
  messages: [],
  ttsAudioBytes: [],
  hasMore: false,
  currentOldestMessageId: undefined,
  lastMessageTime: undefined,
  speechToText: undefined,
  reachedMaxSttUsage: false,
  showChatBox: false,
  focusChatBox: false,
  dmUserIds: undefined,
  isLoading: false,
  fullScreenChatBox: false,
  resizeEvent: false,
  keyboardActive: true,
  selectedMessage: undefined,
};

/**
 * Compare messages considering non-stored STT messages that won't have ID. In this case, fallback the comparison to the STT result ID.
 */
const cmpMessage = (a: ChatMessage, b: ChatMessage) =>
  (a.id && b.id && a.id === b.id) ||
  (a.speechRecognition && b.speechRecognition && a.speechRecognition.id === b.speechRecognition.id);

function isMessageContentEqual(a: ChatMessage, b: ChatMessage): boolean {
  return (
    a.text === b.text &&
    JSON.stringify(a.translationRevision) === JSON.stringify(b.translationRevision) &&
    JSON.stringify(a.reactions) === JSON.stringify(b.reactions) &&
    a.isEdited === b.isEdited &&
    a.retranslation === b.retranslation &&
    a.unread === b.unread &&
    a.time === b.time // ensure you're using the same formatting (ISO string vs timestamp)
  );
}

/**
 * Merges a new STT message into an existing one, ensuring that outdated or partial
 * translations do not overwrite longer, more complete ones.
 *
 * - If the new message has `speechRecognition.isFinal === true`, it fully replaces the old message.
 * - Otherwise, for each language key in `text`, the longer version (existing or new) is retained.
 * - If `text` is not a multilingual object on either side, the new message replaces the old one.
 *
 * @param existingMsg - The current ChatMessage already in state.
 * @param newMsg - The incoming ChatMessage to be merged.
 * @returns A merged ChatMessage with safe translation merging.
 *
 * @note
 * This is necessary because STT results and translation updates arrive separately.
 * In some cases, an older STT message (with outdated translations) arrives after a newer
 * translation update. Without this check, newer translations could be overwritten by
 * shorter or stale ones. This function prevents regression by favoring longer texts unless
 * the message is finalized.
 */
function mergeSttMessages(existingMsg: ChatMessage, newMsg: ChatMessage): ChatMessage {
  const newStt = newMsg.speechRecognition;
  const oldStt = existingMsg.speechRecognition;

  // Case 1: If finalized, always replace
  if (newStt?.isFinal) {
    return newMsg;
  }

  // Case 2: If both have localTs, prefer newer one
  const newLocalTs = newStt?.localTs;
  const oldLocalTs = oldStt?.localTs;

  // NOTE:
  // STT and translation processes run independently, so messages may arrive out of order.
  // There are two common cases to handle carefully:
  //
  // 1. 🕒 Old STT result:
  //    - Use `localTs` to determine recency.
  //    - `localTs` is assigned on the client side when the STT result is received from the STT server.
  //
  // 2. 🌐 Out-of-order translation result:
  //    - Translation results can arrive later with an older `localTs`.
  //    - To avoid discarding valuable translations (e.g., Japanese), compare the number of language keys.
  //    - If the new message contains more languages than the current one, we allow merging even if `localTs` is older.
  //
  //    ✅ Example:
  //      - Existing message: `{ localTs: 10, text: { en: "and what the president did which took us all off guard" } }`
  //      - Incoming older message: `{ localTs: 9, text: { en: "and what the president did which took us all", ja: "そして、大統領が行ったことが私たち全員を巻き込んだんですね。" } }`
  //      - Although `localTs: 9` is older, it includes a `ja` translation that must be preserved.
  //      - In this case, we continue to the merge process instead of skipping.
  const existingLangs = Object.keys(existingMsg.text ?? {});
  const newLangs = Object.keys(newMsg.text ?? {});
  if (
    typeof newLocalTs === "number" &&
    typeof oldLocalTs === "number" &&
    newLocalTs < oldLocalTs &&
    newLangs.length <= existingLangs.length
  ) {
    return existingMsg;
  }

  // Case 3: Merge texts safely by language
  if (
    typeof existingMsg.text !== "object" ||
    typeof newMsg.text !== "object" ||
    existingMsg.text === null ||
    newMsg.text === null
  ) {
    return newMsg;
  }

  const mergedText: Record<string, string> = {};
  const allLangs = new Set([...Object.keys(existingMsg.text), ...Object.keys(newMsg.text)]);

  for (const lang of allLangs) {
    const oldText = existingMsg.text?.[lang];
    const newText = newMsg.text?.[lang];

    if (typeof newText === "string" && typeof oldText === "string") {
      if (newText === oldText) {
        mergedText[lang] = oldText; // No change
      } else {
        // Prefer longer text
        mergedText[lang] = newText.length >= oldText.length ? newText : oldText;
      }
    } else if (typeof newText === "string") {
      mergedText[lang] = newText;
    } else if (typeof oldText === "string") {
      // NOTE:
      // We do NOT overwrite `oldText` with `undefined` if the new message lacks that language.
      // This is critical for preserving translated content like "ja" that may only arrive once,
      // especially since later updates often come with only "en" (e.g., partial STT updates).
      mergedText[lang] = oldText;
    }
  }

  return {
    ...existingMsg,
    ...newMsg,
    text: mergedText,
  };
}

export function textChannelReducer(
  state: TextChannelState = textChannelInitialState,
  action: TextChannelActionTypes,
): TextChannelState {
  switch (action.type) {
    case TextChannelActions.SET_TEXT_CHANNEL:
      return {
        ...state,
        workspaceId: action.payload.workspaceId,
        textChannelId: action.payload.textChannelId,
        messages: [],
        hasMore: false,
        lastMessageTime: undefined,
      };
    case TextChannelActions.GET_MESSAGES:
      return {
        ...state,
        hasMore: false,
        lastMessageTime: action.payload.previousTo,
      };
    case TextChannelActions.GOT_MESSAGES:
      return {
        ...state,
        messages: [...action.payload.messages, ...state.messages],
        hasMore: action.payload.hasMore,
        currentOldestMessageId: action.payload.currentOldestMessageId,
      };
    case TextChannelActions.NEW_MESSAGE: {
      const newMsg = action.payload.message;
      const myself = action.payload.myself;
      const newMessage = calcTsOnMessage(newMsg, myself);

      const index = state.messages.findIndex(m => cmpMessage(m, newMsg));

      // Case 1: Brand new message → just append
      if (index === -1) {
        logE(`Got a new message from api server: ${JSON.stringify(newMessage, null, 2)}`);
        return {
          ...state,
          messages: [...state.messages, newMessage],
        };
      }

      const existingMsg = state.messages[index];
      if (isMessageContentEqual(existingMsg, newMessage)) return state;

      logE(`Got the updated message from api server: ${JSON.stringify(newMessage, null, 2)}`);
      const mergedMessage = mergeSttMessages(existingMsg, newMessage);
      const updatedMessages = [...state.messages.slice(0, index), mergedMessage, ...state.messages.slice(index + 1)];

      return {
        ...state,
        messages: updatedMessages,
      };
    }
    case TextChannelActions.SPEECH_TO_TEXT_INIT:
      return { ...state, speechToText: action.payload };
    case TextChannelActions.UPDATE_VP_STT_TOKEN:
    case TextChannelActions.UPDATE_VP_STT_SERVER:
      return { ...state, speechToText: state.speechToText ? { ...state.speechToText, ...action.payload } : undefined };
    case TextChannelActions.SPEECH_TO_TEXT_STOP:
      return { ...state, speechToText: undefined };
    case TextChannelActions.CLEAR_UNREAD_MESSAGES:
      return { ...state, messages: state.messages.map(el => ({ ...el, unread: false })) };
    case TextChannelActions.CLEAR_SELECTED_UNREAD_MESSAGES:
      return {
        ...state,
        messages: state.messages.map(el => {
          if (action.payload.messageIds.some(id => id === el.id)) {
            return { ...el, unread: false };
          } else {
            return el;
          }
        }),
      };
    case TextChannelActions.SHOW_CHAT_BOX:
      return { ...state, showChatBox: action.payload.isShow };
    case TextChannelActions.SET_FOCUS_CHAT_BOX:
      return { ...state, focusChatBox: action.payload.isFocus };
    case TextChannelActions.SET_DM_USERS:
      if (action.payload.userId) return { ...state, dmUserIds: action.payload.userId };

      return state;
    case TextChannelActions.UPDATE_IS_LOADING:
      return { ...state, isLoading: action.payload.loading };
    case TextChannelActions.TRANSLATED_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(m => {
          return m.id === action.payload.message.id
            ? {
                ...action.payload.message,
                reactions: m.reactions,
              }
            : m;
        }),
      };
    case TextChannelActions.FULL_SCREEN_CHAT_BOX:
      return {
        ...state,
        fullScreenChatBox: action.payload.fullScreen,
      };
    case TextChannelActions.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
      };
    case TextChannelActions.GOT_TEXT_TO_SPEECH:
      return {
        ...state,
        ttsAudioBytes: [...state.ttsAudioBytes, action.payload],
      };
    case TextChannelActions.UPDATE_TEXT_TO_SPEECH:
      return {
        ...state,
        ttsAudioBytes: action.payload.ttsAudioBytes,
      };
    case TextChannelActions.UPDATE_REACHED_MAX_STT_USAGE:
      return {
        ...state,
        reachedMaxSttUsage: action.payload.reachedSttMaxUsage,
      };
    case TextChannelActions.RESIZE_CHAT_BOX:
      return {
        ...state,
        resizeEvent: action.payload.resizeEvent,
      };
    case TextChannelActions.SET_KEYBOARD_ACTIVE:
      return {
        ...state,
        keyboardActive: action.payload.keyboardActive,
      };

    case TextChannelActions.DELETE_MESSAGE:
      const remainMessages = state.messages.filter(a =>
        action.payload.groupIds.length > 0
          ? !action.payload.groupIds.some(g => a.groupId === g)
          : a.id !== action.payload.messageId,
      );

      return {
        ...state,
        messages: remainMessages,
      };

    case TextChannelActions.UPDATE_MESSAGE:
      if (action.payload.groupId) {
        const groupMessages = state.messages.filter(a => action.payload.message.groupId === a.groupId);

        const groupIds = groupMessages.length === 1 ? [] : groupMessages.slice(1).map(a => a.id);

        return {
          ...state,
          messages: state.messages
            .filter(a => !groupIds.some(g => g === a.id))
            .map(m => {
              return m.groupId === action.payload.message.groupId
                ? {
                    ...m,
                    text: action.payload.message.text,
                    originalTextLanguage: action.payload.message.originalTextLanguage,
                  }
                : m;
            }),
        };
      } else {
        return {
          ...state,
          messages: state.messages.map(m => {
            return m.id === action.payload.message.id ? { ...m, ...action.payload.message } : m;
          }),
        };
      }

    case TextChannelActions.ADD_MESSAGE_REACTION:
      const updatedMessages = state.messages.map(message => {
        const reactedMessage = action.payload.messageId
          ? message.id === action.payload.messageId
          : message.groupId === action.payload.groupId;

        if (reactedMessage) {
          const updatedReactions = message.reactions
            ? {
                ...message.reactions,
                [action.payload.emoji]: [
                  ...(message.reactions[action.payload.emoji] || []),
                  {
                    groupId: action.payload.groupId,
                    createdBy: action.payload.createdBy,
                    messageId: action.payload.messageId,
                  },
                ],
              }
            : {
                [action.payload.emoji]: [
                  {
                    groupId: action.payload.groupId,
                    createdBy: action.payload.createdBy,
                    messageId: action.payload.messageId,
                  },
                ],
              };

          return {
            ...message,
            reactions: updatedReactions,
          };
        }

        return message;
      });

      return {
        ...state,
        messages: updatedMessages,
      };
    case TextChannelActions.REMOVE_MESSAGE_REACTION:
      const updatedMessagesAfterRemove = state.messages.map(message => {
        const reactedMessage = !!action.payload.messageId
          ? message.id === action.payload.messageId
          : message.groupId === action.payload.groupId;

        if (reactedMessage && message.reactions) {
          const updatedReactions = { ...message.reactions };

          if (updatedReactions[action.payload.emoji]) {
            updatedReactions[action.payload.emoji] = updatedReactions[action.payload.emoji].filter(
              reaction =>
                (action.payload.messageId
                  ? reaction.messageId !== action.payload.messageId
                  : reaction.groupId !== action.payload.groupId) || reaction.createdBy !== action.payload.createdBy,
            );

            if (updatedReactions[action.payload.emoji].length === 0) {
              delete updatedReactions[action.payload.emoji];
            }

            return {
              ...message,
              reactions: updatedReactions,
            };
          }
        }

        return message;
      });

      return {
        ...state,
        messages: updatedMessagesAfterRemove,
      };

    default:
      return state;
  }
}

function calcTsOnMessage(message: ChatMessage, myself: boolean) {
  return message.speechRecognition
    ? {
        ...message,
        speechRecognition: {
          ...message.speechRecognition,
          localTs: myself && message.speechRecognition.localTs ? message.speechRecognition.localTs : undefined,
        },
      }
    : message;
}
